import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { useAuth } from '@/components/AuthProvider';
import { useMissionPage } from '@/hooks/useMissionPage';
import { submitCreatorFeedback } from '@/lib/firebase/feedback';
import { trackCreatorFeedback } from '@/lib/utils/analytics';

const OPTION_KEYS = ['notReady', 'confused', 'payment', 'busy', 'other'] as const;

function storageKey(uid: string) {
  return `msf_publish_feedback_seen_${uid}`;
}

// MSF-50: a 1-question micro-survey for a signed-up creator who has a draft
// mission page but hasn't published it. Mounted at the app root (App.tsx)
// rather than inside the creator dashboard's own page components: the first
// attempt at this feature (2026-08-13) was wired into
// src/pages/dashboard/CreatorDashboard.tsx, which turned out to be dead code
// (the live "/creator" index route renders src/pages/dashboard/MyPage.tsx),
// so it never rendered for a real creator before a later dead-code sweep
// deleted it. MyPage.tsx and DashboardLayout.tsx are both CLAUDE.md-forbidden
// files, so this gates on the route itself instead of being placed in their
// render tree.
export default function CreatorPublishFeedbackPrompt() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const onCreatorRoute = location.pathname.startsWith('/creator');
  const { missionPage, isLoading } = useMissionPage(onCreatorRoute ? user?.uid : undefined);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Which option the creator picked, kept so an optional free-text follow-up
  // (below) can be tied back to it without firing a second ambiguous event.
  const [answeredKey, setAnsweredKey] = useState<string | null>(null);
  const [detail, setDetail] = useState('');
  // Only 'answer' capture existed before (MSF-50): PostHog had no way to tell
  // "never rendered" apart from "rendered and ignored/dismissed", which left
  // the widget's real reach unmeasurable. Guarded with a ref, not state, so a
  // Firestore snapshot re-render of missionPage can't re-fire the impression.
  const shownTrackedRef = useRef(false);

  useEffect(() => {
    if (!onCreatorRoute || !user?.uid || isLoading) {
      setVisible(false);
      return;
    }
    const alreadySeen = localStorage.getItem(storageKey(user.uid));
    if (!alreadySeen && missionPage && missionPage.isPublished === false) {
      setVisible(true);
      if (!shownTrackedRef.current) {
        shownTrackedRef.current = true;
        trackCreatorFeedback('unpublished_signup', 'shown');
      }
    }
  }, [onCreatorRoute, user?.uid, isLoading, missionPage]);

  if (!visible || !user?.uid) return null;

  const dismiss = () => {
    trackCreatorFeedback('unpublished_signup', 'dismissed');
    localStorage.setItem(storageKey(user.uid), '1');
    setVisible(false);
  };

  const handleAnswer = async (optionKey: string) => {
    // PostHog capture is fire-and-forget and doesn't depend on Firestore write
    // permissions, so it succeeds even while the `creator_feedback` collection's
    // rules aren't deployed yet (see docs/PRIORITY.md "Standing status" — the
    // IAM gap blocking deploy-rules.yml). The Firestore write is still attempted
    // best-effort for when that unblocks, but no longer required for the user
    // to see their answer was received.
    trackCreatorFeedback('unpublished_signup', optionKey);
    localStorage.setItem(storageKey(user.uid), '1');
    setAnsweredKey(optionKey);
    try {
      await submitCreatorFeedback(user.uid, 'unpublished_signup', optionKey);
    } catch (error) {
      console.error('Could not persist creator feedback to Firestore (captured via analytics instead):', error);
    }
  };

  // Optional second step: the preset options tell us WHAT is in the way but
  // not WHY, and this survey has fired for exactly one real creator so far
  // (2026-10-06) with no way to learn more than a single enum value. A free
  // text box costs one extra tap and is skippable, so it never blocks the
  // "your answer was received" confirmation the user already got from
  // handleAnswer above.
  const handleSendDetail = async () => {
    const trimmed = detail.trim();
    setSubmitted(true);
    if (!trimmed || !answeredKey) return;
    trackCreatorFeedback('unpublished_signup', answeredKey, trimmed);
    try {
      await submitCreatorFeedback(user.uid, 'unpublished_signup', answeredKey, trimmed);
    } catch (error) {
      console.error('Could not persist creator feedback detail to Firestore (captured via analytics instead):', error);
    }
  };

  const skipDetail = () => setSubmitted(true);

  return (
    // bottom offset reads the CSS var ConsentBanner publishes (0px when it isn't
    // showing) so this prompt isn't hidden underneath the full-width consent
    // banner for a first-time visitor who hasn't decided yet — see
    // ConsentBanner.tsx's HEIGHT_VAR and DemoMissionPage.tsx's mobile CTA for
    // the same pattern.
    <div
      className="fixed inset-x-0 z-40 border-t border-blue-100 bg-blue-50/95 px-4 py-4 shadow-[0_-2px_12px_rgba(0,0,0,0.06)] backdrop-blur transition-[bottom]"
      style={{ bottom: 'var(--consent-banner-height, 0px)' }}
    >
      <div className="relative mx-auto max-w-3xl">
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('creator.publishFeedback.dismiss')}
          className="absolute right-0 top-0 text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
        {submitted ? (
          <p className="pr-6 text-sm text-gray-700">{t('creator.publishFeedback.thanks')}</p>
        ) : answeredKey ? (
          <>
            <h3 className="pr-6 text-base font-semibold text-gray-900">
              {t('creator.publishFeedback.detailTitle')}
            </h3>
            <Textarea
              className="mt-2 bg-white"
              rows={2}
              maxLength={500}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder={t('creator.publishFeedback.detailPlaceholder')}
            />
            <div className="mt-2 flex gap-2">
              <Button variant="default" size="sm" onClick={handleSendDetail} disabled={!detail.trim()}>
                {t('creator.publishFeedback.send')}
              </Button>
              <Button variant="ghost" size="sm" onClick={skipDetail}>
                {t('creator.publishFeedback.skip')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h3 className="pr-6 text-base font-semibold text-gray-900">
              {t('creator.publishFeedback.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-600">{t('creator.publishFeedback.desc')}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {OPTION_KEYS.map((key) => (
                <Button key={key} variant="outline" size="sm" onClick={() => handleAnswer(key)}>
                  {t(`creator.publishFeedback.options.${key}`)}
                </Button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
